import { createVNode, render } from 'vue'
import LoginDialog from '@/components/feedback/LoginDialog.vue'

export interface LoginDialogOptions {
  closeOnClickOverlay?: boolean
  escToClose?: boolean
  animation?: 'pop' | 'slide-up' | 'flip'
}

interface LoginDialogInstance {
  show: (options?: LoginDialogOptions) => Promise<'success' | 'cancel'>
  hide: () => void
}

class LoginDialogManager {
  private container: HTMLElement | null = null
  private vnode: any = null
  private currentResolve: ((value: 'success' | 'cancel') => void) | null = null
  public appContext: any = null
  private cleanupTimer: number | null = null

  private createContainer() {
    if (!this.container) {
      this.container = document.createElement('div')
      this.container.className = 'login-dialog-container'
      this.container.style.position = 'fixed'
      this.container.style.top = '0'
      this.container.style.left = '0'
      this.container.style.width = '100%'
      this.container.style.height = '100%'
      this.container.style.zIndex = '9'
      document.body.appendChild(this.container)
    }
    return this.container
  }

  private createVNode(props: any) {
    const vnode = createVNode(LoginDialog, {
      ...props,
      'onUpdate:show': (visible: boolean) => {
        if (!visible && this.currentResolve) {
          this.currentResolve('cancel')
          this.currentResolve = null
        }
        if (!visible) {
          this.scheduleCleanUp()
        }
      },
      onSuccess: () => {
        if (this.currentResolve) {
          this.currentResolve('success')
          this.currentResolve = null
        }
        this.scheduleCleanUp()
      }
    })
    if (this.appContext) vnode.appContext = this.appContext
    return vnode
  }

  show(options: LoginDialogOptions = {}): Promise<'success' | 'cancel'> {
    this.hide()
    const container = this.createContainer()
    return new Promise((resolve) => {
      this.currentResolve = resolve
      const props = { show: true, ...options }
      this.vnode = this.createVNode(props)
      render(this.vnode, container)
    })
  }

  hide() {
    this.cleanUp()
    this.currentResolve = null
  }

  private cleanUp() {
    if (this.cleanupTimer !== null) {
      window.clearTimeout(this.cleanupTimer)
      this.cleanupTimer = null
    }
    if (this.vnode && this.container) {
      render(null, this.container)
      this.vnode = null
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
      this.container = null
    }
  }

  private scheduleCleanUp() {
    if (this.cleanupTimer !== null) {
      window.clearTimeout(this.cleanupTimer)
      this.cleanupTimer = null
    }
    // 留出过渡动画时间（需与 CSS 过渡时间匹配，略加余量）
    this.cleanupTimer = window.setTimeout(() => {
      this.cleanUp()
    }, 260)
  }
}

const manager = new LoginDialogManager()

export function useLoginDialog(): LoginDialogInstance {
  return {
    show: (options?: LoginDialogOptions) => manager.show(options),
    hide: () => manager.hide()
  }
}

export const $loginDialog = {
  show: (options?: LoginDialogOptions) => manager.show(options),
  hide: () => manager.hide()
}

export default {
  install(app: any) {
    manager.appContext = app._context
    app.config.globalProperties.$loginDialog = $loginDialog
    app.provide('loginDialog', $loginDialog)
  }
}


